import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@monksflow/monks-ui';
import { isWidgetToolId } from '@inference.engine/agentic-ui/components';
import { widgetQueryKeys } from '@/entities/widget-definition';
import { useTranslation } from 'react-i18next';
import {
  createRunStore,
  traceExtension,
  type RelayChatExtension,
} from '@inference.engine/agentic-react';
import { useUIStore } from '@/app/store/ui';
import { useWidgetStore } from '@/app/store/widget';
import {
  CANVAS_TOOLS_KEY,
  ChatContextRing,
  type BuilderChats,
  type CopilotChatHostProps,
  type TestChatProps,
} from '@/features/agent-builder';
import {
  AgentComposerSettings,
  ChatSessionLayout,
  ChatStoreScope,
  ToolStepTitleProvider,
  useChatStoreApi,
  useChatHistory,
  useCurrentAgent,
  useCurrentAgentId,
  type SendMessageInput,
  type ToolStepTitle,
} from '@/features/chats';
import {
  ChatSession,
  PROJECT_CONTEXT_DEPS,
  type ChatSessionProps,
} from '@/pages/StreamChatSession/ChatSession';
import { notificationService } from '@/lib/services/notification.service';
import { SidePanelContainer } from '@/shared/ui/layout/SidePanel';
import { WidgetCapture } from '@/features/html-widget';

type LazyChatSessionProps = Omit<ChatSessionProps, 'chatId' | 'initialMessage' | 'beforeSend'> & {
  agentId: string;
  /** Opens the conversation on its first message, and returns its id. */
  openSession: () => Promise<string>;
  beforeSend: () => Promise<void>;
  /** Shown when `beforeSend` fails, so the first message was not sent. */
  notSentText: string;
  /**
   * Whether each send reads the agent's card again first, for an agent a save can change (whether
   * it offers suggestions, its greeting). Defaults to true.
   */
  refreshAgent?: boolean;
  /** An existing conversation to open with its history; the first message then opens nothing. */
  chatId?: string;
  /** The existing conversation failed to load or is another agent's. */
  onResumeFailed?: () => void;
  /** Agents whose last answer still makes the existing conversation this chat's; defaults to `agentId`. */
  resumableAgentIds?: readonly string[];
};

const widgetDraftSchema = z.object({ widgetDefinitionId: z.string() });

/** Flow.Assist created a widget, which the Widget agent is about to design. */
const widgetDesignSchema = z.object({ widgetDefinitionId: z.string(), name: z.string() });

/** A canvas edit that attaches a widget, whose name and status only the catalog knows. */
const widgetAttachSchema = z.object({
  op: z.literal('attach_tool'),
  toolId: z.string().refine(isWidgetToolId),
});

/** Undoes what the composer did on a send that never left: its busy state and its cleared text. */
function restoreUnsent(chatStore: ReturnType<typeof useChatStoreApi>, input: SendMessageInput) {
  const state = chatStore.getState();
  state.setUiStatus(null);
  state.setInputValue((current) => current || input.prompt);
  const projectContext = input.inputs?.projectContext;
  if (projectContext === undefined) return;
  state.setInputOptions((current) =>
    current?.projectContext ? current : { ...current, projectContext },
  );
}

/**
 * A conversation opened on its first message, drawn by the chat page's own `ChatSession`. Until
 * then the composer stands alone; the first message opens the conversation and is sent as its
 * first, the way the chat page starts a new chat.
 */
function LazyChatSession({
  agentId,
  openSession,
  beforeSend,
  notSentText,
  refreshAgent = true,
  chatId,
  onResumeFailed,
  resumableAgentIds,
  ...session
}: LazyChatSessionProps) {
  const { t } = useTranslation('chats');
  const chatStore = useChatStoreApi();
  const widgets = session.widgets !== false;
  const [opened, setOpened] = useState<{ chatId: string; first: SendMessageInput } | null>(null);
  const opening = useRef(false);

  // Start the stores this chat writes on the agent it talks to, and hand them back clean. A layout
  // effect, so it runs before the session's own effects: a resumed chat's cached history reaches the
  // store in one of those, and a reset after it would leave the composer unable to send.
  useLayoutEffect(() => {
    const reset = () => {
      chatStore.getState().reset();
      if (!widgets) return;
      useWidgetStore.getState().reset();
      useUIStore.getState().closeSidePanel();
    };
    reset();
    void chatStore.getState().setAgentById(agentId);
    return reset;
  }, [chatStore, agentId, widgets]);

  const prepareSend = async () => {
    await beforeSend();
    if (refreshAgent) await chatStore.getState().reloadCurrentAgent();
  };

  const send = async (input: SendMessageInput) => {
    // A second send while the first opens the conversation would open another, and the
    // reply would land in one while the page holds the other's id.
    if (opening.current) return;
    opening.current = true;
    let prepared = false;
    try {
      await prepareSend();
      prepared = true;
      const chatId = await openSession();
      setOpened({ chatId, first: { ...input, agentId } });
    } catch {
      opening.current = false;
      // No stream will run to end the turn this send opened.
      session.onTurnEnd?.();
      restoreUnsent(chatStore, input);
      notificationService.showError(prepared ? t('root.failedToCreateANew') : notSentText);
    }
  };

  if (chatId) {
    return (
      <ResumedChatSession
        {...session}
        chatId={chatId}
        agentId={agentId}
        beforeSend={prepareSend}
        onResumeFailed={onResumeFailed}
        {...(resumableAgentIds ? { resumableAgentIds } : {})}
      />
    );
  }

  if (!opened) {
    return (
      <ChatSessionLayout
        isNewChat
        projectContextDeps={PROJECT_CONTEXT_DEPS}
        dropzone={session.dropzone ?? true}
        {...(session.footerEnd
          ? {
              footerEnd:
                typeof session.footerEnd === 'function' ? session.footerEnd() : session.footerEnd,
            }
          : {})}
        {...(session.debugControls ? { debugControls: session.debugControls } : {})}
        onSend={send}
      >
        {session.empty}
      </ChatSessionLayout>
    );
  }

  // An admin trying a draft, or asking the Copilot, is not product usage.
  return (
    <ChatSession
      {...session}
      chatId={opened.chatId}
      initialMessage={opened.first}
      beforeSend={prepareSend}
      analytics={false}
    />
  );
}

/**
 * An earlier conversation, opened with its history. One that cannot be loaded, or that another agent
 * answers, is reported so the host can start afresh instead.
 */
function ResumedChatSession({
  agentId,
  onResumeFailed,
  resumableAgentIds = [agentId],
  ...session
}: Omit<ChatSessionProps, 'analytics'> & {
  agentId: string;
  onResumeFailed?: () => void;
  resumableAgentIds?: readonly string[];
}) {
  const history = useChatHistory({ contextId: session.chatId, defaultAgentId: agentId });
  const unusable =
    history.isError || (history.data && !resumableAgentIds.includes(history.data.lastAgentId));
  const onResumeFailedRef = useRef(onResumeFailed);
  onResumeFailedRef.current = onResumeFailed;
  useEffect(() => {
    if (unusable) onResumeFailedRef.current?.();
  }, [unusable]);

  if (unusable) return null;
  return <ChatSession {...session} analytics={false} />;
}

/** The builder's test conversation with the draft. */
function TestChat({ visible, models, effort, ...props }: TestChatProps) {
  const { t } = useTranslation('agentBuilder');
  return (
    <LazyChatSession
      {...props}
      debugControls={{ models, ...(effort ? { effort } : {}) }}
      // Every test turn is traced, so the ring always has the turn's context to show.
      footerEnd={(chat) => (
        <ChatContextRing
          runStore={props.runStore}
          {...(chat ? { chatId: chat.chatId, busy: chat.busy } : {})}
        />
      )}
      showCompactions
      notSentText={t('testDock.notSent')}
      dropzone={visible}
      titleFromFirstMessage={false}
    />
  );
}

function TestWidget() {
  const closeSidePanel = useUIStore((s) => s.closeSidePanel);
  return <SidePanelContainer closeSidePanel={closeSidePanel} type="widget" />;
}

/**
 * Gives the conversation back to Flow.Assist and puts `compose(typed)` in the composer, since
 * switching agents empties it of what the person had typed.
 */
function handBack(
  chatStore: ReturnType<typeof useChatStoreApi>,
  agentId: string,
  compose: (typed: string) => string,
): void {
  const state = chatStore.getState();
  const typed = state.chatInput.value;
  if (state.currentAgentId && state.currentAgentId !== agentId) void state.setAgentById(agentId);
  chatStore.getState().setInputValue(() => compose(typed));
}

/**
 * Inside the Copilot's chat store: tells the builder which agent answers, and while another agent
 * answers in Flow.Assist's place (the Widget agent it handed off to) says so, with the way back.
 */
function CopilotHandOff({
  agentId,
  onAgentChange,
}: Pick<CopilotChatHostProps, 'agentId' | 'onAgentChange'>) {
  const { t } = useTranslation('agentBuilder');
  const chatStore = useChatStoreApi();
  const currentAgentId = useCurrentAgentId();
  const answering = useCurrentAgent()?.title;
  const onAgentChangeRef = useRef(onAgentChange);
  onAgentChangeRef.current = onAgentChange;

  useEffect(() => {
    if (currentAgentId) onAgentChangeRef.current?.(currentAgentId);
  }, [currentAgentId]);

  if (!currentAgentId || currentAgentId === agentId) return null;
  return (
    <div className="mx-sm mt-xs flex shrink-0 items-center gap-xs rounded-md border border-dashed border-border px-sm py-2xs text-sm text-muted-foreground">
      <span className="min-w-0 flex-1 truncate">
        {t('copilot.handOff.answering', { name: answering ?? currentAgentId })}
      </span>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => handBack(chatStore, agentId, (typed) => typed)}
      >
        {t('copilot.handOff.back')}
      </Button>
    </div>
  );
}

/** Inside the Copilot's chat store: puts a prompt the builder asks for in the composer. */
function CopilotPrefill({
  agentId,
  prefill,
  onPrefillUsed,
  resumeChatId,
}: Pick<CopilotChatHostProps, 'agentId' | 'prefill' | 'onPrefillUsed' | 'resumeChatId'>) {
  const chatStore = useChatStoreApi();
  // A resumed chat's history restores the agent that answered last, which empties the composer.
  const history = useChatHistory({
    contextId: resumeChatId ?? '',
    defaultAgentId: agentId,
    enabled: Boolean(resumeChatId),
  });
  const restored = !resumeChatId || Boolean(history.data) || history.isError;

  // No guard of its own: a mount run twice over (StrictMode) clears the store in between, so the
  // second run has to fill it again. Text the person typed stays, and a prompt already there is not
  // added twice. The host drops the prefill once used, which ends the runs.
  useEffect(() => {
    if (!prefill || !restored) return;
    // A request from the builder is Flow.Assist's to answer, even mid hand-off.
    handBack(chatStore, agentId, (typed) => {
      if (typed.includes(prefill.text)) return typed;
      return typed.trim() ? `${typed}\n\n${prefill.text}` : prefill.text;
    });
    onPrefillUsed?.();
  }, [prefill, restored, chatStore, onPrefillUsed, agentId]);

  return null;
}

const COPILOT_RUN = () => ({ debug: true });
/** The agent's own models stay in the picker; only the effort control is added. */
const NO_MODEL_LIST = { models: [] };

function CopilotChat({
  beginTurn,
  onOp,
  onTurnEnd,
  stepTitle,
  empty,
  workspaceId,
  openSession,
  uiContextFor,
  agentId,
  prefill,
  onPrefillUsed,
  onAgentChange,
  onWidgetDesign,
  resumeChatId,
  onResumeFailed,
  resumableAgentIds,
}: CopilotChatHostProps) {
  const { t } = useTranslation('agentBuilder');
  const queryClient = useQueryClient();
  // Its own run store: the builder's lights the canvas for test runs, which these turns are not.
  const [runStore] = useState(createRunStore);
  const onOpRef = useRef(onOp);
  onOpRef.current = onOp;
  const onWidgetDesignRef = useRef(onWidgetDesign);
  onWidgetDesignRef.current = onWidgetDesign;
  const extensions = useMemo<RelayChatExtension[]>(
    () => [
      {
        part: 'data-copilot-op',
        onPart: (data) => {
          onOpRef.current(data);
          if (widgetAttachSchema.safeParse(data).success) {
            void queryClient.invalidateQueries({ queryKey: CANVAS_TOOLS_KEY });
          }
        },
      },
      // Usage of each step, for the context ring.
      traceExtension,
      {
        part: 'data-widget-design',
        onPart: (data) => {
          const design = widgetDesignSchema.safeParse(data);
          if (!design.success) return;
          onWidgetDesignRef.current?.({
            id: design.data.widgetDefinitionId,
            name: design.data.name,
          });
          // A widget created without an agent has no attach op to refresh the palette.
          void queryClient.invalidateQueries({ queryKey: widgetQueryKeys.list() });
          void queryClient.invalidateQueries({ queryKey: CANVAS_TOOLS_KEY });
        },
      },
      // The Widget agent saved a draft: that widget shows, and its preview, checks and thumbnail follow.
      // The palette is left to useCanvasFollowsWidgets, which refetches it only for what it shows.
      {
        part: 'data-widget-draft',
        onPart: (data) => {
          const saved = widgetDraftSchema.safeParse(data);
          if (!saved.success) return;
          const id = saved.data.widgetDefinitionId;
          // A hand-off about a widget that already existed names it only here.
          onWidgetDesignRef.current?.({ id, name: '' });
          for (const queryKey of [
            widgetQueryKeys.list(),
            widgetQueryKeys.detail(id),
            widgetQueryKeys.checks(id),
            widgetQueryKeys.thumbnail(id),
          ]) {
            void queryClient.invalidateQueries({ queryKey });
          }
        },
      },
    ],
    [queryClient],
  );
  const builderContext = useRef<unknown>(undefined);
  const titleStep = useMemo<ToolStepTitle>(
    () =>
      ({ parts }) =>
        stepTitle(parts),
    [stepTitle],
  );

  return (
    <ChatStoreScope>
      <CopilotHandOff agentId={agentId} onAgentChange={onAgentChange} />
      <ToolStepTitleProvider value={titleStep}>
        <LazyChatSession
          agentId={agentId}
          workspaceId={workspaceId}
          runStore={runStore}
          extensions={extensions}
          openSession={openSession}
          {...(resumeChatId ? { chatId: resumeChatId } : {})}
          {...(onResumeFailed ? { onResumeFailed } : {})}
          {...(resumableAgentIds ? { resumableAgentIds } : {})}
          beforeSend={() => {
            builderContext.current = beginTurn().builderContext;
            return Promise.resolve();
          }}
          notSentText={t('copilot.notSent')}
          // Debug turns, like the Test chat's: they take the chosen effort and report the context
          // each step used.
          runOptions={COPILOT_RUN}
          debugControls={NO_MODEL_LIST}
          footerEnd={(chat) => (
            <ChatContextRing
              runStore={runStore}
              {...(chat ? { chatId: chat.chatId, busy: chat.busy } : {})}
            />
          )}
          uiContext={() => uiContextFor(builderContext.current)}
          widgets={false}
          dropzone={false}
          refreshAgent={false}
          titleFromFirstMessage={false}
          onTurnEnd={onTurnEnd}
          empty={empty}
        />
      </ToolStepTitleProvider>
      {/* After the session, so its effect runs after the session's own restore of the history. */}
      <CopilotPrefill
        agentId={agentId}
        prefill={prefill}
        onPrefillUsed={onPrefillUsed}
        {...(resumeChatId ? { resumeChatId } : {})}
      />
    </ChatStoreScope>
  );
}

/** The chats the agent builder talks through. */
export const BUILDER_CHATS: BuilderChats = {
  Test: TestChat,
  Widget: TestWidget,
  useWidgetOpen: () => useUIStore((s) => s.sidePanelType === 'widget'),
  useWidgetFullscreen: () =>
    useUIStore((s) => s.sidePanelType === 'widget' && s.sidePanelLayoutMode === 'fullscreen'),
  Copilot: CopilotChat,
  AgentSettings: AgentComposerSettings,
  WidgetCapture,
};
